import subscriptionsTypes from "./subscriptionsTypes";

const initialState = {
    // List management
    pending: false,
    subscriptions: [],
    totalSubscriptions: 0,
    currentSubscription: null,

    // Statistics (from list endpoint)
    statistics: {
        totalPurchases: 0,
        activeSubscribers: 0,
    },

    // User subscriptions (for transactions modal)
    userSubscriptions: [],
    userSubscriptionsPending: false,

    // Analytics
    analytics: null,
    analyticsPending: false,

    // History data
    purchaseHistory: {
        data: [],
        total: 0,
        pending: false,
    },
    stampUsageHistory: {
        data: [],
        total: 0,
        pending: false,
    },
    subscriptionClients: {
        data: [],
        total: 0,
        pending: false,
    },
};

const subscriptionsReducer = (state = initialState, action) => {
    switch (action.type) {
        // Get subscriptions list
        case subscriptionsTypes.GET_SUBSCRIPTIONS_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.GET_SUBSCRIPTIONS_SUCCESS:
            return {
                ...state,
                pending: false,
                subscriptions: action.payload || [],
                totalSubscriptions: action.payload?.length || 0,
            };
        case subscriptionsTypes.GET_SUBSCRIPTIONS_ERROR:
            return { ...state, pending: false };

        // Create subscription
        case subscriptionsTypes.CREATE_SUBSCRIPTION_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.CREATE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                pending: false,
                subscriptions: [action.payload, ...state.subscriptions],
                totalSubscriptions: state.totalSubscriptions + 1,
            };
        case subscriptionsTypes.CREATE_SUBSCRIPTION_ERROR:
            return { ...state, pending: false };

        // Get subscription details
        case subscriptionsTypes.GET_SUBSCRIPTION_DETAILS_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.GET_SUBSCRIPTION_DETAILS_SUCCESS:
            return {
                ...state,
                pending: false,
                currentSubscription: action.payload,
            };
        case subscriptionsTypes.GET_SUBSCRIPTION_DETAILS_ERROR:
            return { ...state, pending: false };

        // Update subscription
        case subscriptionsTypes.UPDATE_SUBSCRIPTION_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.UPDATE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                pending: false,
                subscriptions: state.subscriptions.map(sub =>
                    sub.id === action.payload.id ? action.payload : sub
                ),
                currentSubscription: action.payload,
            };
        case subscriptionsTypes.UPDATE_SUBSCRIPTION_ERROR:
            return { ...state, pending: false };

        // Delete subscription
        case subscriptionsTypes.DELETE_SUBSCRIPTION_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.DELETE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                pending: false,
                subscriptions: state.subscriptions.filter(sub => sub.id !== action.payload),
                totalSubscriptions: state.totalSubscriptions - 1,
            };
        case subscriptionsTypes.DELETE_SUBSCRIPTION_ERROR:
            return { ...state, pending: false };

        // Activate subscription
        case subscriptionsTypes.ACTIVATE_SUBSCRIPTION_PENDING:
            return { ...state };
        case subscriptionsTypes.ACTIVATE_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                subscriptions: state.subscriptions.map(sub =>
                    sub.id === action.payload.id
                        ? { ...sub, status: action.payload.status }
                        : sub
                ),
                currentSubscription: state.currentSubscription?.id === action.payload.id
                    ? { ...state.currentSubscription, status: action.payload.status }
                    : state.currentSubscription,
            };
        case subscriptionsTypes.ACTIVATE_SUBSCRIPTION_ERROR:
            return { ...state };

        // Suspend subscription
        case subscriptionsTypes.SUSPEND_SUBSCRIPTION_PENDING:
            return { ...state };
        case subscriptionsTypes.SUSPEND_SUBSCRIPTION_SUCCESS:
            return {
                ...state,
                subscriptions: state.subscriptions.map(sub =>
                    sub.id === action.payload.id
                        ? { ...sub, status: action.payload.status }
                        : sub
                ),
                currentSubscription: state.currentSubscription?.id === action.payload.id
                    ? { ...state.currentSubscription, status: action.payload.status }
                    : state.currentSubscription,
            };
        case subscriptionsTypes.SUSPEND_SUBSCRIPTION_ERROR:
            return { ...state };

        // Use stamps
        case subscriptionsTypes.USE_STAMPS_PENDING:
            return { ...state, pending: true };
        case subscriptionsTypes.USE_STAMPS_SUCCESS:
            return {
                ...state,
                pending: false,
                userSubscriptions: state.userSubscriptions.map(sub =>
                    sub.id === action.payload.id
                        ? { ...sub, stamps_remaining: action.payload.stamps_remaining }
                        : sub
                ),
            };
        case subscriptionsTypes.USE_STAMPS_ERROR:
            return { ...state, pending: false };

        // Get user subscriptions
        case subscriptionsTypes.GET_USER_SUBSCRIPTIONS_PENDING:
            return { ...state, userSubscriptionsPending: true };
        case subscriptionsTypes.GET_USER_SUBSCRIPTIONS_SUCCESS:
            return {
                ...state,
                userSubscriptionsPending: false,
                userSubscriptions: action.payload || [],
            };
        case subscriptionsTypes.GET_USER_SUBSCRIPTIONS_ERROR:
            return { ...state, userSubscriptionsPending: false };

        // Get analytics
        case subscriptionsTypes.GET_SUBSCRIPTION_ANALYTICS_PENDING:
            return { ...state, analyticsPending: true };
        case subscriptionsTypes.GET_SUBSCRIPTION_ANALYTICS_SUCCESS:
            return {
                ...state,
                analyticsPending: false,
                analytics: action.payload,
            };
        case subscriptionsTypes.GET_SUBSCRIPTION_ANALYTICS_ERROR:
            return { ...state, analyticsPending: false };

        // Get purchase history
        case subscriptionsTypes.GET_PURCHASE_HISTORY_PENDING:
            return {
                ...state,
                purchaseHistory: { ...state.purchaseHistory, pending: true },
            };
        case subscriptionsTypes.GET_PURCHASE_HISTORY_SUCCESS:
            return {
                ...state,
                purchaseHistory: {
                    data: action.payload?.data || [],
                    total: action.payload?.total || 0,
                    pending: false,
                },
            };
        case subscriptionsTypes.GET_PURCHASE_HISTORY_ERROR:
            return {
                ...state,
                purchaseHistory: { ...state.purchaseHistory, pending: false },
            };

        // Get stamp usage history
        case subscriptionsTypes.GET_STAMP_USAGE_HISTORY_PENDING:
            return {
                ...state,
                stampUsageHistory: { ...state.stampUsageHistory, pending: true },
            };
        case subscriptionsTypes.GET_STAMP_USAGE_HISTORY_SUCCESS:
            return {
                ...state,
                stampUsageHistory: {
                    data: action.payload?.data || [],
                    total: action.payload?.total || 0,
                    pending: false,
                },
            };
        case subscriptionsTypes.GET_STAMP_USAGE_HISTORY_ERROR:
            return {
                ...state,
                stampUsageHistory: { ...state.stampUsageHistory, pending: false },
            };

        // Get subscription clients
        case subscriptionsTypes.GET_SUBSCRIPTION_CLIENTS_PENDING:
            return {
                ...state,
                subscriptionClients: { ...state.subscriptionClients, pending: true },
            };
        case subscriptionsTypes.GET_SUBSCRIPTION_CLIENTS_SUCCESS:
            return {
                ...state,
                subscriptionClients: {
                    data: action.payload?.data || [],
                    total: action.payload?.total || 0,
                    pending: false,
                },
            };
        case subscriptionsTypes.GET_SUBSCRIPTION_CLIENTS_ERROR:
            return {
                ...state,
                subscriptionClients: { ...state.subscriptionClients, pending: false },
            };

        // Clear current subscription
        case subscriptionsTypes.CLEAR_CURRENT_SUBSCRIPTION:
            return { ...state, currentSubscription: null };

        // Clear user subscriptions
        case subscriptionsTypes.CLEAR_USER_SUBSCRIPTIONS:
            return { ...state, userSubscriptions: [] };

        default:
            return state;
    }
};

export default subscriptionsReducer;
